import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Modal,
  Text,
  Button,
  Group,
  Stack,
  Paper,
  Title,
  Alert,
  Switch,
} from "@mantine/core";

function GroupCourseModal({
  isOpen,
  onClose,
  group,
  groupCourses = [],
  availableCourses = [],
  onCoursesUpdated,
}) {
  const { getVerifiedJWT } = useAuth();
  const [loadingStates, setLoadingStates] = useState({});
  const [error, setError] = useState(null);
  const [localGroupCourses, setLocalGroupCourses] = useState(groupCourses);
  const [localAvailableCourses, setLocalAvailableCourses] =
    useState(availableCourses);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local state when props change
  useEffect(() => {
    setLocalGroupCourses(groupCourses);
    setLocalAvailableCourses(availableCourses);
    // Reset changes flag when modal opens with new data
    setHasChanges(false);
  }, [groupCourses, availableCourses]);

  const updateGroupCourse = async (courseId, include, is_sequential) => {
    if (!group?.id || !courseId) return;

    const buttonKey = `${courseId}-${include}`;
    setLoadingStates(prev => ({ ...prev, [buttonKey]: true }));
    setError(null);

    try {
      const jwt = await getVerifiedJWT();
      const payload = {
        group_id: group.id,
        course_id: courseId,
        include: include,
      };

      // Only include is_sequential if it's defined
      if (is_sequential !== undefined) {
        payload.is_sequential = is_sequential;
      }

      const response = await fetch(getApiEndpoint("/v1/groups/group/course"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to update group courses");
      }

      // Update local state immediately on success
      if (include) {
        // Adding course: move from available to group courses with default is_sequential=true
        const courseToAdd = localAvailableCourses.find(
          course => course.id === courseId
        );
        if (courseToAdd) {
          const courseWithSequential = {
            ...courseToAdd,
            is_sequential: is_sequential !== undefined ? is_sequential : true,
          };
          setLocalGroupCourses(prev => [...prev, courseWithSequential]);
          setLocalAvailableCourses(prev =>
            prev.filter(course => course.id !== courseId)
          );
        }
      } else {
        // Removing course: move from group courses to available
        const courseToRemove = localGroupCourses.find(
          course => course.id === courseId
        );
        if (courseToRemove) {
          // Remove is_sequential when moving back to available
          const { is_sequential: _, ...courseWithoutSequential } =
            courseToRemove;
          setLocalAvailableCourses(prev => [...prev, courseWithoutSequential]);
          setLocalGroupCourses(prev =>
            prev.filter(course => course.id !== courseId)
          );
        }
      }

      // Mark that changes have been made
      setHasChanges(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingStates(prev => ({ ...prev, [buttonKey]: false }));
    }
  };

  const handleToggleSequential = async (course, newValue) => {
    // Update the is_sequential setting for an existing course
    await updateGroupCourse(course.id, true, newValue);

    // Update local state immediately
    setLocalGroupCourses(prev =>
      prev.map(c =>
        c.id === course.id ? { ...c, is_sequential: newValue } : c
      )
    );
  };

  const handleAddCourse = course => {
    // When adding a new course, default to sequential mode (true)
    updateGroupCourse(course.id, true, true);
  };

  const handleRemoveCourse = course => {
    updateGroupCourse(course.id, false);
  };

  const handleClose = () => {
    setError(null);

    // Only call the refresh callback if changes were made
    if (hasChanges && onCoursesUpdated) {
      onCoursesUpdated();
    }

    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={`Edit Courses - ${group?.name || "Group"}`}
      size="lg"
      centered
    >
      <Stack gap="lg">
        {/* Error Alert */}
        {error && (
          <Alert variant="light" color="red" title="Error">
            {error}
          </Alert>
        )}

        {/* Group Courses Section */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Group Courses ({localGroupCourses.length})
          </Title>
          {localGroupCourses.length > 0 ? (
            <Stack gap="sm">
              {localGroupCourses.map((course, index) => (
                <Paper
                  key={course.id || index}
                  p="sm"
                  withBorder
                  style={{
                    borderRadius: "8px",
                  }}
                >
                  <Group gap="sm" wrap="nowrap">
                    {course.course_thumbnail && (
                      <img
                        src={course.course_thumbnail}
                        alt={course.course_title}
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "8px",
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                        onError={e => {
                          e.target.style.display = "none";
                        }}
                      />
                    )}
                    <Stack gap={2} style={{ flex: 1 }}>
                      <Text fw={500}>
                        {course.course_title || "Unknown Course"}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {course.course_desc || "No description available"}
                      </Text>
                      <Switch
                        label="Sequential Mode"
                        description="When enabled, videos must be watched in order"
                        size="sm"
                        checked={course.is_sequential !== false}
                        onChange={e =>
                          handleToggleSequential(
                            course,
                            e.currentTarget.checked
                          )
                        }
                        disabled={Object.values(loadingStates).some(
                          loading => loading
                        )}
                      />
                    </Stack>
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={() => handleRemoveCourse(course)}
                      loading={loadingStates[`${course.id}-false`]}
                      disabled={Object.values(loadingStates).some(
                        loading => loading
                      )}
                      style={{ flexShrink: 0 }}
                    >
                      Remove
                    </Button>
                  </Group>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              No courses assigned to this group
            </Text>
          )}
        </Paper>

        {/* Available Courses Section */}
        <Paper p="md" withBorder>
          <Title order={4} mb="md">
            Available Courses ({localAvailableCourses.length})
          </Title>
          {localAvailableCourses.length > 0 ? (
            <Stack gap="sm">
              {localAvailableCourses.map((course, index) => (
                <Group
                  key={course.id || index}
                  gap="sm"
                  p="sm"
                  style={{
                    borderRadius: "8px",
                  }}
                >
                  {course.course_thumbnail && (
                    <img
                      src={course.course_thumbnail}
                      alt={course.course_title}
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                      onError={e => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <Stack gap={2} style={{ flex: 1 }}>
                    <Text fw={500}>
                      {course.course_title || "Unknown Course"}
                    </Text>
                    <Text size="sm" c="dimmed">
                      {course.course_desc || "No description available"}
                    </Text>
                  </Stack>
                  <Button
                    size="xs"
                    variant="light"
                    color="green"
                    onClick={() => handleAddCourse(course)}
                    loading={loadingStates[`${course.id}-true`]}
                    disabled={Object.values(loadingStates).some(
                      loading => loading
                    )}
                  >
                    Add
                  </Button>
                </Group>
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" style={{ fontStyle: "italic" }}>
              No additional courses available
            </Text>
          )}
        </Paper>

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default GroupCourseModal;
